import { forwardRef, useImperativeHandle } from 'react';

import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import { z } from 'zod';

import { Form, FormField } from '@/components';

import { useAccountData } from './AccountDataContext';

// Updated schema to align with API EducationRequest structure
const educationItemSchema = z.object({
  educationLevel: z
    .string()
    .min(
      1,
      t(
        'components:adminCandidates.addCandidate.educationInformation.levelOfEducationRequired'
      )
    ),
  universityName: z.string().optional(),
  department: z.string().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  isStillStudying: z.boolean().optional().default(false),
});

const educationSchema = z.object({
  educations: z.array(educationItemSchema),
});

export type EducationInfoFormFields = z.infer<typeof educationSchema>;
export type EducationItemFormFields = z.infer<typeof educationItemSchema>;

export interface EducationInfoFormRef {
  submit: () => void;
}

export const EducationInfoForm = forwardRef<EducationInfoFormRef>(
  (_props, ref) => {
    const { t } = useTranslation(['common', 'candidate', 'cv', 'components']);
    const { data, updateEducation } = useAccountData();

    // Create proper default education item
    const createDefaultEducation = (): EducationItemFormFields => ({
      educationLevel: '',
      universityName: '',
      department: '',
      startDate: undefined,
      endDate: undefined,
      isStillStudying: false,
    });

    // Transform API data to form format
    const getFormDefaultValues = (): EducationInfoFormFields => {
      if (!data?.education?.length) {
        return {
          educations: [createDefaultEducation()],
        };
      }

      const transformedEducations = data.education.map((edu) => ({
        educationLevel: edu.educationLevel ?? '',
        isStillStudying: edu.isStillStudying ?? false,
        universityName: edu.universityName ?? '',
        department: edu.department ?? '',
        startDate: edu.startDate ? new Date(edu.startDate) : undefined,
        endDate: edu.endDate ? new Date(edu.endDate) : undefined,
      }));

      return { educations: transformedEducations };
    };

    const form = useForm<EducationInfoFormFields>({
      mode: 'onBlur',
      resolver: zodResolver(educationSchema),
      values: getFormDefaultValues(),
    });

    const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: 'educations',
    });

    // Expose submit function to parent via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        form.handleSubmit(handleSubmit)();
      },
    }));

    const handleSubmit = async (formData: EducationInfoFormFields) => {
      // Transform form data to API format - convert dates to strings
      const transformedEducations = formData.educations.map((education) => ({
        ...education,
        startDate: education.startDate?.toISOString(),
        endDate: education.endDate?.toISOString(),
      }));

      await updateEducation(transformedEducations);
    };

    const educationDegreeOptions = [
      {
        value: 'high_school',
        label: t(
          'components:adminCandidates.addCandidate.educationInformation.levels.highSchool'
        ),
      },
      {
        value: 'associate',
        label: t(
          'components:adminCandidates.addCandidate.educationInformation.levels.associate'
        ),
      },
      {
        value: 'bachelor',
        label: t(
          'components:adminCandidates.addCandidate.educationInformation.levels.bachelor'
        ),
      },
      {
        value: 'master',
        label: t(
          'components:adminCandidates.addCandidate.educationInformation.levels.master'
        ),
      },
      {
        value: 'doctoral',
        label: t(
          'components:adminCandidates.addCandidate.educationInformation.levels.doctoral'
        ),
      },
      {
        value: 'professional',
        label: t(
          'components:adminCandidates.addCandidate.educationInformation.levels.professional'
        ),
      },
      {
        value: 'certificate',
        label: t(
          'components:adminCandidates.addCandidate.educationInformation.levels.certificate'
        ),
      },
    ];

    return (
      <Form {...form} onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {fields.map((field, idx) => (
            <Box
              key={field.id}
              borderBottom={idx === fields.length - 1 ? 'none' : '1px solid'}
              borderColor="gray.50"
              pb={4}
            >
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontWeight="semibold" fontSize={16}>
                  {t(
                    'components:adminCandidates.addCandidate.educationInformation.title'
                  )}{' '}
                  {fields.length > 1 ? idx + 1 : ''}
                </Text>
                <Button
                  onClick={() => append(createDefaultEducation())}
                  colorScheme="brand"
                  variant="link"
                  leftIcon={
                    <Icon as={LuPlus} fontSize={20} color="brand.500" />
                  }
                >
                  {t('common:actions.addNew')}
                </Button>
              </Flex>

              <Grid
                templateColumns={{ base: '1fr', md: '1fr 1fr' }}
                gap={4}
                mt={4}
              >
                <FormField
                  control={form.control}
                  name={`educations.${idx}.educationLevel`}
                  label={t(
                    'components:adminCandidates.addCandidate.educationInformation.levelOfEducation'
                  )}
                  placeholder={t(
                    'components:adminCandidates.addCandidate.educationInformation.selectLevelOfEducation'
                  )}
                  type="select"
                  size={{ base: 'sm', md: 'md' }}
                  options={educationDegreeOptions}
                />
                <FormField
                  control={form.control}
                  name={`educations.${idx}.department`}
                  label={t(
                    'components:adminCandidates.addCandidate.educationInformation.section'
                  )}
                  placeholder={t(
                    'components:adminCandidates.addCandidate.educationInformation.section'
                  )}
                  type="text"
                  size={{ base: 'sm', md: 'md' }}
                />
                <FormField
                  control={form.control}
                  name={`educations.${idx}.universityName`}
                  label={t(
                    'components:adminCandidates.addCandidate.educationInformation.university'
                  )}
                  placeholder={t(
                    'components:adminCandidates.addCandidate.educationInformation.university'
                  )}
                  type="text"
                  size={{ base: 'sm', md: 'md' }}
                />
                <HStack gap={2}>
                  <FormField
                    control={form.control}
                    name={`educations.${idx}.startDate`}
                    label={t('common:timeAndDate.startDate')}
                    type="date"
                    size={{ base: 'sm', md: 'md' }}
                  />
                  <FormField
                    control={form.control}
                    name={`educations.${idx}.endDate`}
                    label={t('common:timeAndDate.endDate')}
                    type="date"
                    size={{ base: 'sm', md: 'md' }}
                    isDisabled={form.watch(`educations.${idx}.isStillStudying`)}
                  />
                </HStack>
              </Grid>
              <Flex justify="space-between" mt={4} gap={2}>
                {fields.length > 1 ? (
                  <Button
                    variant="link"
                    color="red.500"
                    onClick={() => remove(idx)}
                    leftIcon={
                      <Icon as={LuTrash2} fontSize={20} color="red.500" />
                    }
                  >
                    {t('common:actions.delete')}
                  </Button>
                ) : (
                  <Box />
                )}
                <FormField
                  type="custom"
                  control={form.control}
                  name={`educations.${idx}.isStillStudying`}
                  render={({ field: { value, ...rest } }) => {
                    return (
                      <HStack>
                        <Switch
                          isChecked={value}
                          colorScheme="brand"
                          size="md"
                          {...rest}
                        />
                        <Text fontSize={14}>
                          {t(
                            'components:adminCandidates.addCandidate.educationInformation.stillStudying'
                          )}
                        </Text>
                      </HStack>
                    );
                  }}
                />
              </Flex>
            </Box>
          ))}
        </VStack>
      </Form>
    );
  }
);

EducationInfoForm.displayName = 'EducationInfoForm';
